import { faker } from '@faker-js/faker/locale/en';
import {
  seasons,
  groups,
  suppliers,
  contactPersons,
  brandOptions,
  categories,
} from './stylesFixtureData';
import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';

// Split out of styles.ts purely to keep that file under the repo's 800-line cap (same pattern
// as stylesBulkEditFixtures.ts / stylesSortFixtureData.ts / stylesCustomFieldDefinitions.ts).
// The bulk-generated 150-row base fixture plus the one deterministic audit-log/persistence row.

/** Matches StyleStateColors keys in constants/styleState — all states are seeded. */
const STYLE_STATES = [
  'unpublished',
  'published',
  'workinprogress',
  'delivered',
  'cancelled',
  'deleted',
];

/** Name parts guarantee search/text-filter fixtures: "Basic" (adj) and "shirt" (type). */
const styleAdjectives = [
  'Basic',
  'Classic',
  'Premium',
  'Casual',
  'Slim',
  'Relaxed',
  'Vintage',
  'Modern',
];
const styleTypes = ['Shirt', 'Trousers', 'Jacket', 'Dress', 'Skirt', 'Sweater', 'Coat', 'Blouse'];

/**
 * CF 707 ("Sustainability Rating") is scoped to Group=Menswear(71) AND Category=Tops(87),
 * mirroring `StyleCustomFieldScopeValidator.AppliesToStyle`'s AND-across-configured-dimensions
 * rule (`Src/Delogue.Application/Styles/CustomFields/StyleCustomFieldScopeValidator.cs`): a
 * style applies only when EVERY dimension the field actually carries associations for is
 * matched — brand/group each OR-match within their own list, but a field configured on
 * multiple dimensions requires all of them to pass. Here only group + category are
 * configured, so a style must be Menswear AND carry the Tops category.
 */
const SUSTAINABILITY_SCOPE = { groupId: 71, categoryId: 87 };
const computeSustainabilityAppliesToStyle = (
  groupId: number | null | undefined,
  categoryIds: number[]
): boolean =>
  groupId === SUSTAINABILITY_SCOPE.groupId && categoryIds.includes(SUSTAINABILITY_SCOPE.categoryId);

export const GENERATED_STYLES_TOTAL = 150;

faker.seed(4242);

const generateStyle = (i: number): StyleRow => {
  const adjective = styleAdjectives[i % styleAdjectives.length];
  const type = styleTypes[Math.floor(i / styleAdjectives.length) % styleTypes.length];
  // Decouple brand from the name (adjective) so that — because the table sorts by
  // name (adjective-first) — every brand's styles are spread across the sort order.
  // Keying brand on `i % len` would tie each brand to one adjective and cluster a
  // multi-brand filter's visible (virtualised) rows into a single brand.
  const brand = brandOptions[Math.floor(i / styleAdjectives.length) % brandOptions.length];
  const contact = contactPersons[i % contactPersons.length];
  const supplier = suppliers[i % suppliers.length];
  const supplierContact = contactPersons[(i + 1) % contactPersons.length];
  const categoryCount = i % 3; // 0, 1 or 2 categories
  const selectedCategories = Array.from(
    { length: categoryCount },
    (_, c) => categories[(i + c) % categories.length]
  );
  // Spread lastUpdated across ~150 days, deterministic per row (no runtime clock).
  const day = String((i % 28) + 1).padStart(2, '0');
  const month = String((i % 12) + 1).padStart(2, '0');
  const lastUpdated = `2025-${month}-${day}T10:00:00`;

  // Every row carries an explicit entry for the scoped "Sustainability Rating" (CF 707) CF —
  // `appliesToStyle` must be present (not just absent-defaults-true) so the FE's
  // `makeScopedCell` renders "Not applicable" for out-of-scope rows instead of defaulting to
  // applies (see `getRowCustomFieldApplies` in cellRenderers.tsx). No rating value is
  // pre-assigned on the generic seed; the pinned demo rows below override this with a value.
  const sustainabilityApplies = computeSustainabilityAppliesToStyle(
    groups[i % groups.length].id,
    selectedCategories.map(c => c.id)
  );
  const customFields: CustomFieldRowEntry[] = [
    {
      id: 707,
      type: 'allowedvalue',
      value: null,
      appliesToStyle: sustainabilityApplies,
    },
  ];

  return {
    id: i,
    name: `${adjective} ${type} ${i}`,
    customId: `ST-${1000 + i}`,
    description: faker.helpers.maybe(() => faker.commerce.productDescription()) ?? null,
    state: STYLE_STATES[i % STYLE_STATES.length],
    logo: i % 4 === 0 ? `https://picsum.photos/seed/delogue-style-${i}/96/96` : '',
    season: seasons[i % seasons.length],
    group: groups[i % groups.length],
    brand: {
      id: brand.id,
      customId: brand.userDefinedId ?? '',
      name: brand.name,
      contactPersonId: contact.id,
      contactPersonName: contact.name,
    },
    supplier: {
      id: supplier.id,
      customId: supplier.customId,
      name: supplier.name,
      contactPersonId: supplierContact.id,
      contactPersonName: supplierContact.name,
    },
    categories: selectedCategories,
    styleColors: [],
    customFields,
    properties: [],
    lastUpdated,
    readyForExport: i % 2 === 0,
  };
};

export const buildGeneratedStyles = (): StyleRow[] =>
  Array.from({ length: GENERATED_STYLES_TOTAL }, (_, i) => generateStyle(i + 1));

/**
 * Deterministic fixture for the audit-log + persistence-after-refresh checks (Testmo
 * 2373100 / 2373073, DL-61932/DL-61933). Prepended to the generated rows (like the
 * DL-61875 ids 1/2 pin below) so it lands on the mocked page-1 slice with a stable
 * id/description regardless of faker output.
 */
export const auditPersistenceFixtureStyle = (): StyleRow => ({
  id: 90001,
  name: 'Audit Trail Fixture Style',
  customId: 'ST-90001',
  description: 'Original description for persistence and audit checks.',
  state: 'unpublished',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
});
